/**
 * Rebalances — moves the desk already made.
 *
 * **Recording, never executing.** Nothing on this page places an order anywhere. An operator
 * bought coin from a counterparty by whatever means they normally use, and this is how the
 * books learn the rate they actually got. Automating the desk is out of scope; getting the
 * ledger to agree with it is not.
 *
 * The form asks for what was given and what was received, and derives the rate from the two.
 * It deliberately does not ask for the rate: a typed rate can disagree with the typed amounts,
 * and then the books carry a price that never happened.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { TreasuryRebalanceKind } from '../contracts/types.js';
import { Button, Card, EmptyState, ErrorBox, Field, Input, LoadingState, Select } from '../components/ui.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { decimalsOf, formatMoney, parseMinorUnits } from '../lib/money.js';
import { relativeTime } from '../lib/format.js';

/**
 * What an operator may say they gave or got.
 *
 * USDC was missing, and it is not a coin the platform merely knows about: it has two deposit
 * networks in the shared catalogue, so it shows up as its own row on the treasury holdings
 * table. A desk that bought USDC over the counter could see the position and had no way to
 * tell the books where it came from.
 *
 * USD is deliberately still absent. The ledger knows the code, but there is no dollar rail in
 * or out, so there is no dollar position for a desk to have traded.
 */
const CURRENCIES = ['NGN', 'USDT', 'USDC', 'BTC', 'ETH', 'SOL'] as const;
/**
 * What each kind is, in the words somebody would use out loud.
 *
 * The stored values are `otc_buy`, `otc_sell` and so on, and "OTC" is trade jargon meaning a
 * deal done directly with a counterparty rather than on an exchange. Nobody reading this page
 * needs that word to record what they did, and a dropdown that demands it is one they pick
 * from by guessing.
 */
const KIND_LABELS: Record<TreasuryRebalanceKind, string> = {
  otc_buy: 'I bought coin',
  otc_sell: 'I sold coin',
  deposit: 'I moved coin in',
  withdrawal: 'I moved coin out',
  correction: 'I am fixing a mistake',
};

const KINDS: readonly TreasuryRebalanceKind[] = [
  'otc_buy',
  'otc_sell',
  'deposit',
  'withdrawal',
  'correction',
];

export function TreasuryRebalancesPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.listTreasuryRebalances(), []);

  const [kind, setKind] = useState<TreasuryRebalanceKind>('otc_buy');
  const [fromCurrency, setFromCurrency] = useState<string>('NGN');
  const [fromAmount, setFromAmount] = useState('');
  const [toCurrency, setToCurrency] = useState<string>('USDT');
  const [toAmount, setToAmount] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fromMinor = parseMinorUnits(fromAmount, decimalsOf(fromCurrency));
  const toMinor = parseMinorUnits(toAmount, decimalsOf(toCurrency));
  const sameCurrency = fromCurrency === toCurrency;
  const ready =
    fromMinor !== null && toMinor !== null && fromMinor > 0n && toMinor > 0n && !sameCurrency;

  async function record(): Promise<void> {
    if (fromMinor === null || toMinor === null) return;
    setSaving(true);
    try {
      await api.recordTreasuryRebalance({
        kind,
        from: { currency: fromCurrency, amount: fromMinor.toString() },
        to: { currency: toCurrency, amount: toMinor.toString() },
        counterparty,
        note,
      } as Parameters<typeof api.recordTreasuryRebalance>[0]);
      toast.push('success', 'Written down.');
      setFromAmount('');
      setToAmount('');
      setCounterparty('');
      setNote('');
      state.reload();
    } catch (error) {
      toast.push('error', 'That was not written down.', messageOf(error));
    } finally {
      setSaving(false);
    }
  }

  if (state.loading) return <LoadingState label="Loading" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const rows = state.data?.rebalances ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Coin you bought or sold yourself</h1>
        <p className="page-head__sub">
          When you buy or sell coin outside GenEx, tell your books about it here. Nothing on this page buys or sells anything: it only writes down what you already did, so your records match what you really hold.
        </p>
      </div>

      {canWrite && (
        <Card title="Write down a trade you made">
          <Field label="What did you do?">
            <Select value={kind} onChange={(e) => setKind(e.target.value as TreasuryRebalanceKind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="What you gave away">
            <Select value={fromCurrency} onChange={(e) => setFromCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={fromAmount}
              onChange={(e) => setFromAmount(e.target.value)}
            />
          </Field>

          <Field label="What you got back">
            <Select value={toCurrency} onChange={(e) => setToCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={toAmount}
              onChange={(e) => setToAmount(e.target.value)}
            />
          </Field>

          {sameCurrency && (
            <p className="muted">
              Pick two <em>different</em> things. Giving away naira and getting naira back is not
              a trade, and there is nothing for your books to learn from it.
            </p>
          )}

          <Field label="Who you traded with">
            <Input
              value={counterparty}
              maxLength={120}
              placeholder="The person or exchange"
              onChange={(e) => setCounterparty(e.target.value)}
            />
          </Field>
          <Field label="Anything worth remembering">
            <Input
              value={note}
              maxLength={512}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>

          <Button variant="primary" disabled={!ready} loading={saving} onClick={() => void record()}>
            Write it down
          </Button>
          <p className="muted">
            You do not type a price. It is worked out from the two amounts above, so the price in your records can never disagree with the amounts it came from.
          </p>
        </Card>
      )}

      <Card title="What you have written down">
        {rows.length === 0 ? (
          <EmptyState
            glyph="–"
            title="Nothing written down yet"
            hint="Once you buy or sell coin yourself, write it down here so your records stay right."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>What happened</th>
                  <th className="num">Given</th>
                  <th className="num">Received</th>
                  <th>Traded with</th>
                  <th>Written down by</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.reference}</td>
                    <td>{KIND_LABELS[r.kind] ?? r.kind.replace(/_/g, ' ')}</td>
                    <td className="num">{formatMoney(r.from)}</td>
                    <td className="num">{formatMoney(r.to)}</td>
                    <td>{r.counterparty === '' ? <span className="muted">Not recorded</span> : r.counterparty}</td>
                    <td>{r.recordedBy ?? <span className="muted">Not recorded</span>}</td>
                    <td>{relativeTime(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
